import { pluralize } from 'utils/pluralize';
import type {
  Entry,
  EntrySide,
  Layout,
  ReaderAssignment,
  SiteLayoutRevision,
  Zone,
} from './types';

export const OUTSIDE_LABEL = 'Outside';

export const NEW_ZONE_NAME = 'New zone';
export const NEW_ENTRY_NAME = 'New entry';

/** Names compare as the layout compares them: trimmed, case-insensitively. */
const normalized = (name: string): string => name.trim().toLowerCase();

/** Adds a numeric suffix until `base` is unused in `list`. */
export const uniqueName = (
  list: Array<{ name: string }>,
  base: string,
): string => {
  const taken = new Set(list.map((item) => normalized(item.name)));
  let candidate = base;
  let suffix = 2;
  while (taken.has(normalized(candidate))) {
    candidate = `${base} ${suffix}`;
    suffix += 1;
  }
  return candidate;
};

export const zoneNameOf = (layout: Layout, zoneId: string | null): string => {
  if (zoneId == null) return OUTSIDE_LABEL;
  return (
    layout.zones.find((zone) => zone.id === zoneId)?.name ?? 'Unknown zone'
  );
};

export const sideFacingLabel = (layout: Layout, side: EntrySide): string =>
  side.kind === 'OUTSIDE' ? OUTSIDE_LABEL : zoneNameOf(layout, side.zoneId);

/** "Outside ↔ Yard": what the two sides of an entry join. */
export const entryJoinLabel = (layout: Layout, entry: Entry): string =>
  `${sideFacingLabel(layout, entry.sideA)} ↔ ${sideFacingLabel(
    layout,
    entry.sideB,
  )}`;

export const otherSide = (entry: Entry, side: EntrySide): EntrySide =>
  side.position === 'A' ? entry.sideB : entry.sideA;

/** "Outside → Yard (entry into Yard)": the crossing a reader on this side records. */
export const sideCrossingLabel = (
  layout: Layout,
  entry: Entry,
  side: EntrySide,
): string => {
  const from = sideFacingLabel(layout, otherSide(entry, side));
  const into = sideFacingLabel(layout, side);
  const note = side.kind === 'OUTSIDE' ? '(exit)' : `(entry into ${into})`;
  return `${from} → ${into} ${note}`;
};

/** Both sides face the same zone, or both face outside. */
export const entryFacesSameZone = (entry: Entry): boolean =>
  entry.sideA.kind === entry.sideB.kind &&
  entry.sideA.zoneId === entry.sideB.zoneId;

export const entriesFacingZone = (layout: Layout, zoneId: string): Entry[] =>
  layout.entries.filter(
    (entry) => entry.sideA.zoneId === zoneId || entry.sideB.zoneId === zoneId,
  );

export const isDuplicateName = (
  list: Array<{ id: string; name: string }>,
  item: { id: string; name: string },
): boolean =>
  list.some(
    (other) =>
      other.id !== item.id && normalized(other.name) === normalized(item.name),
  );

export type PublishCheckItem = {
  type: 'zone' | 'entry';
  id: string;
  label: string;
};
export type PublishBlocker = { text: string; items: PublishCheckItem[] };
export type PublishWarning = { text: string; names?: string[] };
export type PublishChecks = {
  blocking: PublishBlocker[];
  warnings: PublishWarning[];
  canPublish: boolean;
};

/**
 * What stands between a draft and publication. Blocking items must be fixed;
 * warnings are shown and may be published through.
 * `siteReaders` are the reader devices assigned to the site, placed or not,
 * including those Avigilon no longer lists for this site (`missing`), which
 * cannot be placed.
 */
export const publishChecks = (
  draft: Layout,
  siteReaders: Array<{
    id: string;
    name: string;
    missing?: boolean;
    notConnected?: boolean;
  }>,
): PublishChecks => {
  const blocking: PublishBlocker[] = [];
  const warnings: PublishWarning[] = [];

  if (!draft.zones.length) {
    blocking.push({
      text: 'The layout must have at least one zone.',
      items: [],
    });
  }
  draft.zones.forEach((zone) => {
    if (!zone.name.trim()) {
      blocking.push({
        text: 'A zone has no name.',
        items: [{ type: 'zone', id: zone.id, label: 'Open zone' }],
      });
    } else if (isDuplicateName(draft.zones, zone)) {
      blocking.push({
        text: `Two zones are named ${zone.name.trim()}.`,
        items: [{ type: 'zone', id: zone.id, label: `Open ${zone.name}` }],
      });
    }
  });
  draft.entries.forEach((entry) => {
    if (!entry.name.trim()) {
      blocking.push({
        text: 'An entry has no name.',
        items: [{ type: 'entry', id: entry.id, label: 'Open entry' }],
      });
    } else if (isDuplicateName(draft.entries, entry)) {
      blocking.push({
        text: `Two entries are named ${entry.name.trim()}.`,
        items: [{ type: 'entry', id: entry.id, label: `Open ${entry.name}` }],
      });
    }
    if (entryFacesSameZone(entry)) {
      blocking.push({
        text: `${
          entry.name || 'An entry'
        } faces the same zone on both sides. An entry must join two different zones, or a zone and Outside.`,
        items: [
          {
            type: 'entry',
            id: entry.id,
            label: `Open ${entry.name || 'entry'}`,
          },
        ],
      });
    }
  });

  const placedIds = new Set(
    draft.readerAssignments.map((assignment) => assignment.readerDeviceId),
  );
  const unplaced = siteReaders.filter(
    (reader) => !reader.missing && !placedIds.has(reader.id),
  );
  if (unplaced.length) {
    warnings.push({
      text: `${pluralize(unplaced.length, 'reader')} assigned to this jobsite ${
        unplaced.length === 1 ? 'is' : 'are'
      } not placed on any entry. ${
        unplaced.length === 1 ? 'It' : 'They'
      } will not report zone events.`,
      names: unplaced.map((reader) => reader.name),
    });
  }
  // A placed reader Avigilon no longer lists for this site reports no zone
  // events, and publishing still succeeds.
  const missing = siteReaders.filter(
    (reader) => reader.missing && placedIds.has(reader.id),
  );
  if (missing.length) {
    warnings.push({
      text: `${pluralize(missing.length, 'placed reader')} ${
        missing.length === 1 ? 'is' : 'are'
      } not listed for this site in Avigilon. ${
        missing.length === 1 ? 'It' : 'They'
      } will not report zone events while ${
        missing.length === 1 ? "it isn't" : "they aren't"
      }.`,
      names: missing.map((reader) => reader.name),
    });
  }
  // Delivery skips a placed reader no Avigilon reader is connected to, and
  // publishing still succeeds.
  const notConnected = siteReaders.filter(
    (reader) =>
      reader.notConnected && !reader.missing && placedIds.has(reader.id),
  );
  if (notConnected.length) {
    warnings.push({
      text: `${pluralize(notConnected.length, 'placed reader')} ${
        notConnected.length === 1 ? 'is' : 'are'
      } not connected to Avigilon. ${
        notConnected.length === 1 ? 'It' : 'They'
      } will not report zone events until ${
        notConnected.length === 1 ? 'it is' : 'they are'
      }.`,
      names: notConnected.map((reader) => reader.name),
    });
  }
  const zonesWithoutEntries = draft.zones.filter(
    (zone) => !entriesFacingZone(draft, zone.id).length,
  ).length;
  if (zonesWithoutEntries) {
    warnings.push({
      text: `${pluralize(zonesWithoutEntries, 'zone')} ${
        zonesWithoutEntries === 1 ? 'has' : 'have'
      } no entries facing ${zonesWithoutEntries === 1 ? 'it' : 'them'}.`,
    });
  }

  return { blocking, warnings, canPublish: blocking.length === 0 };
};

/** The name the layout API gives the placed reader. */
export const readerAssignmentName = (assignment: ReaderAssignment): string =>
  assignment.readerName.trim() || 'Reader name unavailable';

/** Human-readable changes from one revision to the next, for the revision banner. */
export const diffLayouts = (
  previous: Layout | null,
  current: Layout,
): string[] => {
  if (!previous) {
    return [
      `First revision: ${pluralize(current.zones.length, 'zone')}, ${pluralize(
        current.entries.length,
        'entry',
        'entries',
      )}, ${pluralize(current.readerAssignments.length, 'reader')} placed.`,
    ];
  }
  const out: string[] = [];
  const byId = <T extends { id: string }>(items: T[]): Map<string, T> =>
    new Map(items.map((item) => [item.id, item]));
  const prevZones = byId(previous.zones);
  const curZones = byId(current.zones);
  const prevEntries = byId(previous.entries);
  const curEntries = byId(current.entries);
  const prevAssignments = new Map(
    previous.readerAssignments.map((assignment) => [
      assignment.readerDeviceId,
      assignment,
    ]),
  );
  const curAssignments = new Map(
    current.readerAssignments.map((assignment) => [
      assignment.readerDeviceId,
      assignment,
    ]),
  );

  current.zones.forEach((zone) => {
    const before = prevZones.get(zone.id);
    if (!before) out.push(`Zone added: ${zone.name}`);
    else if (before.name !== zone.name)
      out.push(`Zone renamed: ${before.name} → ${zone.name}`);
  });
  previous.zones.forEach((zone) => {
    if (!curZones.has(zone.id)) out.push(`Zone removed: ${zone.name}`);
  });
  const facing = (entry: Entry): string =>
    `${entry.sideA.kind}:${entry.sideA.zoneId}|${entry.sideB.kind}:${entry.sideB.zoneId}`;
  current.entries.forEach((entry) => {
    const before = prevEntries.get(entry.id);
    if (!before)
      out.push(
        `Entry added: ${entry.name} (${entryJoinLabel(current, entry)})`,
      );
    else {
      if (facing(before) !== facing(entry))
        out.push(
          `Entry re-sided: ${entry.name}, now ${entryJoinLabel(
            current,
            entry,
          )}`,
        );
      if (before.name !== entry.name)
        out.push(`Entry renamed: ${before.name} → ${entry.name}`);
    }
  });
  previous.entries.forEach((entry) => {
    if (!curEntries.has(entry.id)) out.push(`Entry removed: ${entry.name}`);
  });
  const placementText = (
    layout: Layout,
    assignment: { entryId: string; admitsIntoSideId: string },
  ): string => {
    const entry = layout.entries.find(
      (candidate) => candidate.id === assignment.entryId,
    );
    if (!entry) return 'an entry';
    const side =
      entry.sideA.id === assignment.admitsIntoSideId
        ? entry.sideA
        : entry.sideB;
    return `${entry.name} → ${sideFacingLabel(layout, side)}`;
  };
  current.readerAssignments.forEach((assignment) => {
    const before = prevAssignments.get(assignment.readerDeviceId);
    if (!before)
      out.push(
        `Reader placed: ${readerAssignmentName(assignment)} (${placementText(
          current,
          assignment,
        )})`,
      );
    else if (
      before.entryId !== assignment.entryId ||
      before.admitsIntoSideId !== assignment.admitsIntoSideId
    ) {
      out.push(
        `Reader moved: ${readerAssignmentName(assignment)}, now ${placementText(
          current,
          assignment,
        )}`,
      );
    }
  });
  previous.readerAssignments.forEach((assignment) => {
    if (!curAssignments.has(assignment.readerDeviceId))
      out.push(`Reader removed: ${readerAssignmentName(assignment)}`);
  });
  if (!out.length) out.push('No changes to zones, entries, or readers.');
  return out;
};

export const effectiveRevision = (
  revisions: SiteLayoutRevision[],
): SiteLayoutRevision | null =>
  revisions.length ? revisions[revisions.length - 1] : null;

/** A zone's or entry's standing in the draft against the effective revision. */
export type DraftTag = 'NEW' | 'CHANGED' | null;

export const draftTagOf = (
  effective: Layout | null,
  collection: 'zones' | 'entries',
  item: Zone | Entry,
): DraftTag => {
  if (!effective) return 'NEW';
  const published = (effective[collection] as Array<Zone | Entry>).find(
    (candidate) => candidate.id === item.id,
  );
  if (!published) return 'NEW';
  return JSON.stringify(published) === JSON.stringify(item) ? null : 'CHANGED';
};

/** "Placed: North Gate → Yard" for a reader in this layout, or "Not placed". */
export const readerPlacementLabel = (
  layout: Layout,
  readerDeviceId: string,
): string => {
  const assignment = layout.readerAssignments.find(
    (candidate) => candidate.readerDeviceId === readerDeviceId,
  );
  if (!assignment) return 'Not placed';
  const entry = layout.entries.find(
    (candidate) => candidate.id === assignment.entryId,
  );
  if (!entry) return 'Not placed';
  const side =
    entry.sideA.id === assignment.admitsIntoSideId ? entry.sideA : entry.sideB;
  return `Placed: ${entry.name} → ${sideFacingLabel(layout, side)}`;
};
